import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, Search, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, fmtDateTime } from "@/lib/api";
import TableToolbar, { useVisibleColumns } from "@/components/TableToolbar";

const ALL_COLUMNS = [
  { key: "name", label: "Garage", alwaysOn: true },
  { key: "city", label: "City" },
  { key: "users", label: "Users" },
  { key: "jc_week", label: "JC this week" },
  { key: "last_activity", label: "Last activity" },
  { key: "status", label: "Status", alwaysOn: true },
];

const VIEWS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "dormant", label: "Dormant" },
  { key: "suspended", label: "Suspended" },
];

const StatusPill = ({ status }) => {
  const map = {
    active: { bg: "#12B76A15", fg: "#12B76A", label: "Active" },
    dormant: { bg: "#F7900915", fg: "#F79009", label: "Dormant" },
    suspended: { bg: "#e6394615", fg: "#e63946", label: "Suspended" },
  }[status] || { bg: "#66708515", fg: "#667085", label: status };
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: map.bg, color: map.fg }}>
      {map.label}
    </span>
  );
};

export default function AdminGarages() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [view, setView] = useState("all");
  const [visibleCols, toggleCol] = useVisibleColumns("admin_garages", ALL_COLUMNS);

  const load = () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (view !== "all" && view !== "dormant") params.status_f = view;
    api.get("/admin/garages", { params })
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load();   }, [view]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
     
  }, [q]);

  const rows = view === "dormant" ? items.filter((g) => g.status === "dormant") : items;

  const isVisible = (k) => ALL_COLUMNS.find((c) => c.key === k)?.alwaysOn || visibleCols.has(k);

  return (
    <div className="space-y-5" data-testid="admin-garages-root">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Platform</div>
        <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557]">Garages</h1>
        <p className="text-[13px] text-[#667085] mt-0.5">Every workshop on the Fender platform.</p>
      </div>

      <TableToolbar
        testIdPrefix="admin-garages"
        searchValue={q}
        onSearchChange={setQ}
        searchPlaceholder="Search name, phone, city…"
        views={VIEWS}
        activeView={view}
        onViewChange={setView}
        columns={ALL_COLUMNS}
        visibleColumns={visibleCols}
        onToggleColumn={toggleCol}
      />

      {/* Desktop */}
      <div className="hidden md:block bg-white border border-[#E4E7EC] rounded-lg overflow-hidden" data-testid="admin-garages-table">
        <table className="w-full text-[13px]">
          <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Garage</th>
              {isVisible("city") && <th className="text-left px-4 py-3 font-semibold">City</th>}
              {isVisible("users") && <th className="text-right px-4 py-3 font-semibold">Users</th>}
              {isVisible("jc_week") && <th className="text-right px-4 py-3 font-semibold">JC / week</th>}
              {isVisible("last_activity") && <th className="text-left px-4 py-3 font-semibold">Last activity</th>}
              <th className="text-left px-4 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1FAEE]">
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-[13px] text-[#667085]">
                  No garages match this view.
                </td>
              </tr>
            )}
            {rows.map((g) => (
              <tr
                key={g.id}
                className="hover:bg-[#F7F9FC] cursor-pointer"
                data-testid={`admin-garage-row-${g.id}`}
                onClick={() => navigate(`/admin/garages/${g.id}`)}
              >
                <td className="px-4 py-3">
                  <Link
                    to={`/admin/garages/${g.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-2 text-[#1d3557] hover:text-[#e63946] font-semibold"
                    data-testid={`admin-garage-link-${g.id}`}
                  >
                    <Building2 size={14} className="text-[#457b9d]" />
                    {g.name}
                  </Link>
                  <div className="text-[11px] text-[#667085] font-mono mt-0.5">{g.id.slice(0, 8)}…</div>
                </td>
                {isVisible("city") && <td className="px-4 py-3 text-[12px] text-[#667085]">{g.city || "—"}</td>}
                {isVisible("users") && <td className="px-4 py-3 text-right font-mono text-[#1d3557]">{g.user_count}</td>}
                {isVisible("jc_week") && <td className="px-4 py-3 text-right font-mono text-[#e63946] font-semibold">{g.jc_this_week}</td>}
                {isVisible("last_activity") && <td className="px-4 py-3 text-[11px] font-mono text-[#667085]">{g.last_activity ? fmtDateTime(g.last_activity) : "—"}</td>}
                <td className="px-4 py-3"><StatusPill status={g.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="md:hidden space-y-2.5" data-testid="admin-garages-cards">
        {rows.map((g) => (
          <Link
            key={g.id}
            to={`/admin/garages/${g.id}`}
            data-testid={`admin-garage-card-${g.id}`}
            className="block bg-white rounded-lg border border-[#E4E7EC] p-3.5 active:bg-[#F7F9FC]"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-semibold text-[#1d3557] truncate flex items-center gap-1">
                  {g.name} <ArrowUpRight size={12} className="text-[#a8dadc]" />
                </div>
                <div className="text-[11px] text-[#667085] mt-0.5">{g.city || "—"} · {g.user_count} users · {g.jc_this_week} JCs this week</div>
              </div>
              <StatusPill status={g.status} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
